import { apiClient } from './client';
import { Project, ProjectTeamMember, ProjectRole, ProjectBudgetLine, ProjectAmendment, Approval, WorkflowStateEntry, ProjectIndicator } from '../types/projects';

export interface CreateProjectInput {
  project_name: string;
  description?: string;
  project_type: string;
  programme_id: number;
  donor_id: number;
  sector_id: number;
  subsector_id: number;
  start_date: string;
  end_date: string;
  budget_total: number;
  currency_code: string;
  country_id: number;
  project_manager_id: number;
}

export interface UpdateProjectInput {
  project_name: string;
  description?: string | null;
  project_type: string;
  start_date: string;
  end_date: string;
  budget_total: number;
  currency_code: string;
  project_manager_id: number;
}

export const projectsApi = {
  list: (filters: { status?: string; programmeId?: number; donorId?: number; search?: string } = {}) => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v).map(([k, v]) => [k, String(v)]));
    return apiClient.get<{ data: Project[] }>(`/api/projects${params.toString() ? `?${params}` : ''}`);
  },
  activeNames: () => apiClient.get<{ data: { id: number; project_reference: string; project_name: string }[] }>('/api/projects/active-names'),
  get: (id: number) => apiClient.get<{ data: Project; auditTrail: WorkflowStateEntry[] }>(`/api/projects/${id}`),
  create: (input: CreateProjectInput) => apiClient.post<{ data: { id: number; project_reference: string } }>('/api/projects', input),
  update: (id: number, input: UpdateProjectInput) => apiClient.put<{ data: Project }>(`/api/projects/${id}`, input),
  remove: (id: number) => apiClient.delete<void>(`/api/projects/${id}`),

  listRoles: () => apiClient.get<{ data: ProjectRole[] }>('/api/projects/roles'),

  listTeam: (id: number) => apiClient.get<{ data: ProjectTeamMember[] }>(`/api/projects/${id}/team`),
  addTeamMember: (id: number, input: { userId: number; projectRoleId?: number | null }) =>
    apiClient.post<{ data: unknown }>(`/api/projects/${id}/team`, input),
  removeTeamMember: (id: number, userId: number) => apiClient.delete<{ data: unknown }>(`/api/projects/${id}/team/${userId}`),

  listBudgetLines: (id: number) => apiClient.get<{ data: ProjectBudgetLine[] }>(`/api/projects/${id}/budget-lines`),
  addBudgetLine: (id: number, input: {
    category: string; description: string; remarks?: string; unitDescription?: string;
    unitQuantity: number; unitCost: number; currencyCode: string;
  }) => apiClient.post<{ data: { id: number; lineCode: string } }>(`/api/projects/${id}/budget-lines`, input),

  listIndicators: (id: number) => apiClient.get<{ data: ProjectIndicator[] }>(`/api/projects/${id}/indicators`),

  submit: (id: number) => apiClient.post<{ data: { status: string; approversAssigned: number } }>(`/api/projects/${id}/submit`),
  decide: (id: number, input: { approvalId: number; decision: 'approved' | 'rejected'; comment?: string }) =>
    apiClient.post<{ data: { decision: string } }>(`/api/projects/${id}/decide`, input),
  close: (id: number) => apiClient.post<{ data: { status: string } }>(`/api/projects/${id}/close`),

  listAmendments: (id: number) => apiClient.get<{ data: ProjectAmendment[] }>(`/api/projects/${id}/amendments`),
  createAmendment: (id: number, input: { amendmentType: string; description: string; oldValues: Record<string, unknown>; newValues: Record<string, unknown> }) =>
    apiClient.post<{ data: { id: number } }>(`/api/projects/${id}/amendments`, input),

  pendingApprovals: () => apiClient.get<{ data: (Approval & { project_reference: string | null; project_name: string | null; budget_total: string | null; currency_code: string | null })[] }>('/api/projects/approvals/pending'),
  recentActivity: () => apiClient.get<{ data: (WorkflowStateEntry & { project_reference: string | null; project_name: string | null })[] }>('/api/projects/activity/recent'),
};

export const amendmentsApi = {
  submit: (id: number) => apiClient.post<{ data: { status: string } }>(`/api/amendments/${id}/submit`),
  decide: (id: number, input: { decision: 'approved' | 'rejected' | 'returned'; comment?: string }) =>
    apiClient.post<{ data: { decision: string } }>(`/api/amendments/${id}/decide`, input),
};

export type { Approval };
